import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

const API_BASE = import.meta.env['VITE_API_URL'] ?? 'http://localhost:3200';

function authHeaders() {
  const token = JSON.parse(localStorage.getItem('urs-auth') ?? '{}')?.state?.token ?? '';
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

function fmtDate(s: string) {
  return new Date(s).toLocaleString('en-UG', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

interface User {
  id: string;
  fullName: string;
  email: string;
  role: string;
  isActive: boolean;
  isLocked: boolean;
  createdAt: string;
  lastLoginAt?: string;
  posModeDefault?: 'table' | 'counter' | null;
}

interface AuditLog {
  id: string;
  action: string;
  entityType?: string;
  entityId?: string;
  createdAt: string;
  user?: { fullName: string; email: string };
  ipAddress?: string;
}

interface Device {
  id: string;
  deviceName?: string;
  deviceType?: string;
  isActive: boolean;
  lastSeenAt?: string;
  user?: { fullName: string };
}

interface JobTitle {
  id: string;
  name: string;
  isActive: boolean;
}

function Skeleton({ h = 56 }: { h?: number }) {
  return (
    <div style={{
      height: h, borderRadius: 8,
      background: 'var(--surface2)', animation: 'pulse 1.5s infinite',
    }} />
  );
}

function roleLabel(r: string, jobTitles: JobTitle[]) {
  const match = jobTitles.find(j => j.name.toLowerCase().replace(/\s+/g, '_') === r.toLowerCase().replace(/\s+/g, '_'));
  if (match) return match.name;
  return r.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function roleBadge(r: string) {
  const l = r.toLowerCase().replace(/\s+/g, '_');
  if (l === 'system_admin') return 'badge-red';
  if (l === 'restaurant_manager' || l === 'floor_manager') return 'badge-blue';
  if (l === 'head_chef' || l === 'chef') return 'badge-amber';
  if (l === 'accountant' || l === 'hr_manager') return 'badge-green';
  return 'badge-gray';
}

function UserModal({
  initial,
  jobTitles,
  onClose,
  onSave,
  onResetPassword,
  isSaving,
}: {
  initial?: Partial<User>;
  jobTitles: JobTitle[];
  onClose: () => void;
  onSave: (data: object) => void;
  onResetPassword?: () => void;
  isSaving: boolean;
}) {
  const [fullName, setFullName] = useState(initial?.fullName ?? '');
  const [email, setEmail] = useState(initial?.email ?? '');
  const initialRole = (() => {
    if (!initial?.role) return '';
    const stored = initial.role.toLowerCase().replace(/\s+/g, '_');
    return jobTitles.find(j => j.name.toLowerCase().replace(/\s+/g, '_') === stored)?.name ?? initial.role;
  })();
  const [role, setRole] = useState(initialRole);
  const [password, setPassword] = useState('');
  const [posModeDefault, setPosModeDefault] = useState<'table' | 'counter' | ''>(initial?.posModeDefault ?? '');

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 440 }}>
        <div className="modal-header">
          <h2 className="modal-title">{initial?.id ? 'Edit User' : 'New User'}</h2>
          <button className="modal-close" onClick={onClose} disabled={isSaving}>&times;</button>
        </div>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="field">
            <label className="field-label">Full Name *</label>
            <input className="input" value={fullName} onChange={e => setFullName(e.target.value)} placeholder="e.g. Amara Nakato" />
          </div>
          <div className="field">
            <label className="field-label">Email *</label>
            <input className="input" type="email" value={email} onChange={e => setEmail(e.target.value)} />
          </div>
          <div className="field">
            <label className="field-label">Role *</label>
            <select className="input" value={role} onChange={e => setRole(e.target.value)}>
              <option value="">— Select role —</option>
              {jobTitles.filter(j => j.isActive).map(j => <option key={j.id} value={j.name}>{j.name}</option>)}
            </select>
          </div>
          <div className="field">
            <label className="field-label">POS Default Mode</label>
            <select className="input" value={posModeDefault} onChange={e => setPosModeDefault(e.target.value as 'table' | 'counter' | '')}>
              <option value="">Show picker on each login</option>
              <option value="table">Table service (skip picker)</option>
              <option value="counter">Counter / walk-in (skip picker)</option>
            </select>
            <p style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4 }}>If set, the POS mode picker is skipped and this mode is used automatically.</p>
          </div>
          {!initial?.id && (
            <div className="field">
              <label className="field-label">Password *</label>
              <input className="input" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Min 8 characters" />
            </div>
          )}
        </div>
        <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-ghost" onClick={onClose} disabled={isSaving}>Cancel</button>
            {onResetPassword && (
              <button className="btn btn-ghost" style={{ color: 'var(--amber)' }} onClick={onResetPassword} disabled={isSaving}>Reset Password</button>
            )}
          </div>
          <button
            className="btn btn-primary"
            onClick={() => {
              if (fullName.trim() && email.trim() && role && (initial?.id || password.length >= 8)) {
                const data: Record<string, unknown> = { fullName: fullName.trim(), email: email.trim(), role, posModeDefault: posModeDefault || null };
                if (!initial?.id) data.password = password;
                onSave(data);
              }
            }}
            disabled={!fullName.trim() || !email.trim() || !role || (!initial?.id && password.length < 8) || isSaving}
          >
            {isSaving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

const PASSWORD_RULES = [
  { label: 'At least 8 characters', test: (p: string) => p.length >= 8 },
  { label: 'Uppercase letter', test: (p: string) => /[A-Z]/.test(p) },
  { label: 'Lowercase letter', test: (p: string) => /[a-z]/.test(p) },
  { label: 'Number', test: (p: string) => /[0-9]/.test(p) },
  { label: 'Special character', test: (p: string) => /[^A-Za-z0-9]/.test(p) },
];

function ResetPasswordModal({
  user,
  onClose,
  onSave,
  isSaving,
}: {
  user: User;
  onClose: () => void;
  onSave: (password: string) => void;
  isSaving: boolean;
}) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);

  const rules = PASSWORD_RULES.map(r => ({ ...r, ok: r.test(password) }));
  const allPassed = rules.every(r => r.ok);
  const matches = password === confirm && confirm.length > 0;
  const canSave = allPassed && matches;

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 420 }}>
        <div className="modal-header">
          <h2 className="modal-title">Reset Password</h2>
          <button className="modal-close" onClick={onClose} disabled={isSaving}>&times;</button>
        </div>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <p style={{ fontSize: 13, color: 'var(--text-3)' }}>
            Setting a new password for <strong style={{ color: 'var(--text)' }}>{user.fullName}</strong>.
          </p>
          <div className="field">
            <label className="field-label">New Password *</label>
            <div style={{ position: 'relative' }}>
              <input
                className="input"
                type={show ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Enter new password"
                style={{ paddingRight: 64 }}
              />
              <button
                type="button"
                onClick={() => setShow(s => !s)}
                style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--text-3)' }}
              >
                {show ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {rules.map(r => (
              <div key={r.label} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                <span style={{ color: password.length === 0 ? 'var(--text-3)' : r.ok ? 'var(--green)' : 'var(--red)', fontWeight: 600, width: 14 }}>
                  {password.length === 0 ? '·' : r.ok ? '✓' : '✗'}
                </span>
                <span style={{ color: password.length === 0 ? 'var(--text-3)' : r.ok ? 'var(--text)' : 'var(--text-3)' }}>{r.label}</span>
              </div>
            ))}
          </div>
          <div className="field">
            <label className="field-label">Confirm Password *</label>
            <input
              className="input"
              type={show ? 'text' : 'password'}
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              placeholder="Re-enter password"
              style={{ borderColor: confirm.length > 0 && !matches ? 'var(--red)' : undefined }}
            />
            {confirm.length > 0 && !matches && (
              <p style={{ fontSize: 11, color: 'var(--red)', marginTop: 4 }}>Passwords do not match</p>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose} disabled={isSaving}>Cancel</button>
          <button className="btn btn-primary" onClick={() => canSave && onSave(password)} disabled={!canSave || isSaving}>
            {isSaving ? 'Saving…' : 'Reset Password'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function AdminPage() {
  const qc = useQueryClient();
  const [activeTab, setActiveTab] = useState<'users' | 'audit' | 'devices'>('users');
  const [userModal, setUserModal] = useState<{ open: boolean; editing?: User }>({ open: false });
  const [resetPwModal, setResetPwModal] = useState<{ open: boolean; user?: User }>({ open: false });
  const [auditPage, setAuditPage] = useState(1);
  const PAGE_SIZE = 25;

  const { data: users = [], isLoading: usersLoading } = useQuery<User[]>({
    queryKey: ['admin-users'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/v1/admin/users`, { headers: authHeaders() });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error?.message ?? 'Failed');
      return j.data ?? [];
    },
  });

  const { data: jobTitles = [] } = useQuery<JobTitle[]>({
    queryKey: ['job-titles'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/v1/hr/job-titles`, { headers: authHeaders() });
      const j = await res.json();
      return j.data ?? [];
    },
  });

  const { data: auditData, isLoading: auditLoading } = useQuery<{ logs: AuditLog[]; total: number }>({
    queryKey: ['audit-logs', auditPage],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/v1/admin/audit-logs?page=${auditPage}&pageSize=${PAGE_SIZE}`, { headers: authHeaders() });
      const j = await res.json();
      return { logs: j.data?.logs ?? [], total: j.data?.total ?? 0 };
    },
    enabled: activeTab === 'audit',
  });

  const { data: devices = [], isLoading: devicesLoading } = useQuery<Device[]>({
    queryKey: ['devices'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/v1/admin/devices`, { headers: authHeaders() });
      const j = await res.json();
      return j.data ?? [];
    },
    enabled: activeTab === 'devices',
  });

  const saveUser = useMutation({
    mutationFn: async ({ id, data }: { id?: string; data: object }) => {
      const res = await fetch(
        id ? `${API_BASE}/v1/admin/users/${id}` : `${API_BASE}/v1/admin/users`,
        { method: id ? 'PATCH' : 'POST', headers: authHeaders(), body: JSON.stringify(data) },
      );
      const j = await res.json();
      if (!res.ok) throw new Error(j.error?.message ?? 'Save failed');
      return j.data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin-users'] }); setUserModal({ open: false }); },
  });

  const unlockUser = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${API_BASE}/v1/admin/users/${id}/unlock`, { method: 'POST', headers: authHeaders() });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error?.message ?? 'Failed');
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-users'] }),
  });

  const resetPassword = useMutation({
    mutationFn: async ({ id, password }: { id: string; password: string }) => {
      const res = await fetch(`${API_BASE}/v1/admin/users/${id}/reset-password`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ password }) });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error?.message ?? 'Failed');
    },
    onSuccess: () => setResetPwModal({ open: false }),
  });

  const wipeDevice = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${API_BASE}/v1/admin/devices/${id}/wipe`, { method: 'POST', headers: authHeaders() });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error?.message ?? 'Failed');
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['devices'] }),
  });

  const activeCount = users.filter(u => u.isActive).length;
  const lockedCount = users.filter(u => u.isLocked).length;

  return (
    <div>
      <div className="page-header">
        <div>
          <p style={{ color: 'var(--text-3)', fontSize: 13 }}>
            {users.length} users · {activeCount} active · {lockedCount} locked
          </p>
        </div>
        {activeTab === 'users' && (
          <button className="btn btn-primary" onClick={() => setUserModal({ open: true })}>+ New User</button>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, background: 'var(--surface2)', borderRadius: 10, padding: 4, width: 'fit-content', marginBottom: 20 }}>
        {(['users', 'audit', 'devices'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '6px 18px', borderRadius: 8, fontSize: 13, fontWeight: activeTab === tab ? 600 : 400,
              background: activeTab === tab ? 'var(--surface)' : 'transparent',
              color: activeTab === tab ? 'var(--text)' : 'var(--text-3)',
              border: 'none', cursor: 'pointer',
              boxShadow: activeTab === tab ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
              transition: 'all 0.12s',
            }}
          >
            {tab === 'users' ? 'Users' : tab === 'audit' ? 'Audit Log' : 'Devices'}
          </button>
        ))}
      </div>

      {/* Users */}
      {activeTab === 'users' && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {usersLoading ? (
            <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} />)}
            </div>
          ) : users.length === 0 ? (
            <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>No users found.</div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Role</th>
                    <th>Last Login</th>
                    <th style={{ textAlign: 'center' }}>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {users.map(user => (
                    <tr key={user.id}>
                      <td>
                        <p style={{ fontWeight: 500, color: 'var(--text)' }}>{user.fullName}</p>
                        <p style={{ fontSize: 11, color: 'var(--text-3)' }}>{user.email}</p>
                      </td>
                      <td>
                        <span className={`badge ${roleBadge(user.role)}`}>{roleLabel(user.role, jobTitles)}</span>
                      </td>
                      <td style={{ color: 'var(--text-3)', fontSize: 12 }}>{user.lastLoginAt ? fmtDate(user.lastLoginAt) : 'Never'}</td>
                      <td style={{ textAlign: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                          <span className={`badge ${user.isActive ? 'badge-green' : 'badge-gray'}`}>{user.isActive ? 'Active' : 'Inactive'}</span>
                          {user.isLocked && <span className="badge badge-red">Locked</span>}
                        </div>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
                          <button className="btn btn-ghost btn-sm" onClick={() => setUserModal({ open: true, editing: user })}>Edit</button>
                          <button className="btn btn-ghost btn-sm" onClick={() => setResetPwModal({ open: true, user })}>Reset Password</button>
                          {user.isLocked && (
                            <button className="btn btn-ghost btn-sm" style={{ color: 'var(--green)' }} onClick={() => unlockUser.mutate(user.id)}>Unlock</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Audit Log */}
      {activeTab === 'audit' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            {auditLoading ? (
              <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
                {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} h={48} />)}
              </div>
            ) : !auditData?.logs.length ? (
              <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>No audit logs found.</div>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>User</th>
                      <th>Action</th>
                      <th>Entity</th>
                      <th>IP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditData.logs.map(log => (
                      <tr key={log.id}>
                        <td style={{ color: 'var(--text-3)', fontSize: 11, whiteSpace: 'nowrap' }}>{fmtDate(log.createdAt)}</td>
                        <td>
                          <p style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-2)' }}>{log.user?.fullName ?? 'System'}</p>
                          <p style={{ fontSize: 11, color: 'var(--text-3)' }}>{log.user?.email}</p>
                        </td>
                        <td>
                          <span style={{ background: 'var(--surface2)', color: 'var(--text-2)', padding: '2px 7px', borderRadius: 5, fontSize: 11, fontFamily: 'var(--font-mono)' }}>{log.action}</span>
                        </td>
                        <td style={{ color: 'var(--text-3)', fontSize: 12 }}>
                          {log.entityType ? `${log.entityType}${log.entityId ? ` #${log.entityId.slice(0, 8)}` : ''}` : '—'}
                        </td>
                        <td style={{ color: 'var(--text-3)', fontSize: 12 }}>{log.ipAddress ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          {auditData && auditData.total > PAGE_SIZE && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 13, color: 'var(--text-3)' }}>
              <span>Showing {((auditPage - 1) * PAGE_SIZE) + 1}–{Math.min(auditPage * PAGE_SIZE, auditData.total)} of {auditData.total}</span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-ghost btn-sm" onClick={() => setAuditPage(p => Math.max(1, p - 1))} disabled={auditPage === 1}>Prev</button>
                <button className="btn btn-ghost btn-sm" onClick={() => setAuditPage(p => p + 1)} disabled={auditPage * PAGE_SIZE >= auditData.total}>Next</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Devices */}
      {activeTab === 'devices' && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {devicesLoading ? (
            <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} h={48} />)}
            </div>
          ) : devices.length === 0 ? (
            <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>No registered devices.</div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Device</th>
                    <th>Assigned To</th>
                    <th>Last Seen</th>
                    <th style={{ textAlign: 'center' }}>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {devices.map(device => (
                    <tr key={device.id}>
                      <td>
                        <p style={{ fontWeight: 500, color: 'var(--text)' }}>{device.deviceName ?? 'Unnamed Device'}</p>
                        <p style={{ fontSize: 11, color: 'var(--text-3)' }}>{device.deviceType ?? '—'}</p>
                      </td>
                      <td style={{ color: 'var(--text-2)' }}>{device.user?.fullName ?? '—'}</td>
                      <td style={{ color: 'var(--text-3)', fontSize: 12 }}>{device.lastSeenAt ? fmtDate(device.lastSeenAt) : 'Never'}</td>
                      <td style={{ textAlign: 'center' }}>
                        <span className={`badge ${device.isActive ? 'badge-green' : 'badge-red'}`}>{device.isActive ? 'Active' : 'Wiped'}</span>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {device.isActive && (
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => { if (confirm('Wipe this device? This will force the POS app to log out.')) wipeDevice.mutate(device.id); }}
                          >
                            Wipe
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {userModal.open && (
        <UserModal
          initial={userModal.editing}
          jobTitles={jobTitles}
          onClose={() => setUserModal({ open: false })}
          onSave={data => saveUser.mutate({ id: userModal.editing?.id, data })}
          onResetPassword={userModal.editing ? () => { setUserModal({ open: false }); setResetPwModal({ open: true, user: userModal.editing }); } : undefined}
          isSaving={saveUser.isPending}
        />
      )}

      {resetPwModal.open && resetPwModal.user && (
        <ResetPasswordModal
          user={resetPwModal.user}
          onClose={() => setResetPwModal({ open: false })}
          onSave={password => resetPassword.mutate({ id: resetPwModal.user!.id, password })}
          isSaving={resetPassword.isPending}
        />
      )}
    </div>
  );
}
